/** Mirrors of the C# plantilla and salary-type DTO records, field for field. */

import type { PositionRequest } from '../positions/positionTypes';

/** Which salary type a rate belongs to. Serialized as an int, matching the C# enum. */
export const SALARY_TYPE = {
  general: 0,
  health: 1,
} as const;

export type SalaryTypeValue = (typeof SALARY_TYPE)[keyof typeof SALARY_TYPE];

/** The one place the two salary types are named, so no surface invents its own wording. */
export function salaryTypeLabel(value: SalaryTypeValue | null | undefined): string {
  return value === SALARY_TYPE.health ? 'Health' : 'General';
}

/**
 * Whether a tranche is in force — the C# `SalaryGradeActivationStatus`, by number. Only an Active
 * tranche is read by another module; a Draft is Salary Grades' alone.
 */
export const SALARY_ACTIVATION = {
  draft: 0,
  active: 1,
} as const;

export type SalaryActivationValue = (typeof SALARY_ACTIVATION)[keyof typeof SALARY_ACTIVATION];

/** The one place the two activation statuses are named. */
export function salaryActivationLabel(value: SalaryActivationValue | null | undefined): string {
  return value === SALARY_ACTIVATION.active ? 'Active' : 'Draft';
}

export interface PlantillaPositionListItem {
  id: string;
  /** The catalog title this post was drawn from. Null only on a legacy post. */
  positionId: string | null;
  title: string;
  /** The post's own permanent id, e.g. "PROG-26001". Assigned by the server. */
  code: string;
  /** The catalog title's short code, e.g. "PROG". Live, not a snapshot. */
  positionCode: string | null;
  /** What the post's grade and step currently pay. Null with no grade, or no rate published. */
  monthlyAmount: number | null;
  orgUnitId: string;
  orgUnitName: string;
  salaryGrade: number | null;
  salaryGradeType: SalaryTypeValue | null;
  salaryStep: number | null;
  /** The grade as one value, e.g. "SG 11-3 (Health)". Absent when the post carries no grade. */
  salaryGradeLabel: string | null;
  description: string | null;
  sortOrder: number;
  /** Live slots. Derived server-side from the items, never stored. */
  authorizedSlots: number;
  /** Occupied slots. Zero until the Personnel phase gives a slot someone to hold. */
  filledSlots: number;
  vacantSlots: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface PlantillaItemListItem {
  id: string;
  plantillaPositionId: string;
  itemNumber: string;
  sequenceNumber: number;
  isFilled: boolean;
  /**
   * The holder, while the slot is filled. **No name** — `personId` is resolved in one batch by
   * `usePersonNames`. All three are absent for a reader without `personnel.read` (the server
   * strips them); `personId` alone is absent when the holder is above the reader's access level.
   */
  personnelId?: string | null;
  personId?: string | null;
  isRestricted?: boolean;
  employeeNumber?: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface PlantillaPositionOption {
  id: string;
  title: string;
  code: string;
  orgUnitName: string;
}

/**
 * Where a new post's designation comes from — EXACTLY one of the two, and the server refuses
 * both or neither.
 */
type PlantillaPositionSource =
  | {
      /**
       * The catalog title this post is drawn from. The title itself is not sent: the server
       * copies it from the position, so no client can authorize a post under a designation the
       * catalog does not hold.
       */
      positionId: string;
      newPosition?: never;
    }
  | {
      /**
       * A title to add to the catalog and draw this post from, written in the same save.
       * Needs `positions.create` on top of `plantilla.create`.
       */
      newPosition: PositionRequest;
      positionId?: never;
    };

export type CreatePlantillaPositionRequest = PlantillaPositionSource & {
  /*
    No code. The plantilla id is {PositionCode}-{YY}{NNN} and the server assigns it inside
    the write — only it knows the next series number at that moment. The form previews it
    through plantillaApi.nextCode.
  */
  /*
    No grade, no order, no description. The grade travels with the chosen title — the server
    copies it, so no client can authorize a post at a grade its designation does not carry.
    The order is assigned server-side. The description is not collected any more, and a field
    the form does not show must not be sendable, or it would be blanked on the next save.
  */
  /**
   * One entry per UNIT the post is authorized in (19 Oct 2026) — a post per unit, or the slots
   * added to the post a unit already has (needs manage-slots). Saved together or not at all.
   */
  units: PlantillaUnitSlotsInput[];
};

/** One unit of a plantilla create, and its TYPED item numbers — unique across every card. */
export interface PlantillaUnitSlotsInput {
  orgUnitId: string;
  itemNumbers: string[];
}

/** The code is absent on purpose: changing it would strand the existing item numbers. */
export interface UpdatePlantillaPositionRequest {
  /** Re-pointing at a different title re-takes the snapshot. The only thing that changes it. */
  positionId: string;
  orgUnitId: string;
  // No grade, order or description, for the reasons the create request gives. Re-pointing at
  // another title re-takes both snapshots; an edit is not a reorder; and it cannot clear what
  // it does not carry.
}

export interface SalaryGradeListItem {
  id: string;
  type: SalaryTypeValue;
  grade: number;
  step: number;
  tranche: number;
  /** An ISO date with no time — a rate takes effect on a day, not at an instant. */
  effectivityDate: string;
  monthlyAmount: number;
  description: string | null;
  activationStatus: SalaryActivationValue;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * One grade a position can be pegged to.
 *
 * Distinct grade/step pairs rather than catalog rows: a post is authorized at a grade, and
 * which tranche currently pays what is not a property of the post.
 */
export interface SalaryGradeOption {
  type: SalaryTypeValue;
  grade: number;
  step: number;
  currentMonthlyAmount: number;
}

/** One tranche of one salary type — the index row. Read off its rates, not stored. */
export interface SalaryTrancheListItem {
  type: SalaryTypeValue;
  tranche: number;
  /** An ISO date with no time. */
  effectivityDate: string;
  gradeCount: number;
  rateCount: number;
  /** Draft or Active — only Active is read by another module. */
  activationStatus: SalaryActivationValue;
  /** False when every rate in it is deleted. */
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface SalaryTrancheDetail extends SalaryTrancheListItem {
  rates: SalaryGradeListItem[];
}

/** One grade of the tranche grid. Null is an empty cell — a rate of 0.00 is a value. */
export interface SalaryGradeLineInput {
  grade: number | null;
  /** The monthly rate at step 1 through 8, by position. */
  stepRates: (number | null)[];
}

/**
 * A tranche's rates, declared together. Every row on screen is sent, blanks included, so a
 * refusal names the cell the reader is looking at; the server drops the blanks.
 */
export interface CreateSalaryGradesRequest {
  type: SalaryTypeValue;
  tranche: number;
  /** An ISO date with no time — one for the whole tranche. */
  effectivityDate: string | null;
  /** Ignored when the tranche already exists — added grades take its status. */
  activationStatus: SalaryActivationValue | null;
  items: SalaryGradeLineInput[];
}
